import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { servicesContent } from '../data/servicesContent';

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

/* A single clickable editorial index row (no cards): number, name, price, preview */
const ServiceRow = ({ s }) => (
  <button
    type="button"
    onClick={() => scrollToId(s.anchor)}
    data-testid={`index-row-${s.anchor}`}
    className="group w-full text-left border-t border-neutral-300 py-5 flex items-start gap-4 transition-colors hover:bg-neutral-900/[0.03]"
  >
    <span className="flex-1 min-w-0">
      <span className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="text-lg lg:text-xl text-neutral-900" style={{ fontWeight: 500, letterSpacing: '0.01em' }}>
          {s.name}
        </span>
        {s.price && <span className="text-sm text-neutral-900 font-medium whitespace-nowrap">{s.price}</span>}
      </span>
      <span className="block text-sm text-neutral-600 mt-1 leading-relaxed">{s.preview}</span>
    </span>
    <ArrowUpRight
      size={18}
      strokeWidth={1.5}
      className="shrink-0 mt-1 text-neutral-400 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-neutral-900"
    />
  </button>
);

const ServiceColumn = ({ titleLine1, titleLine2, desc, services, testid }) => (
  <div data-testid={testid}>
    <span className="block h-px w-12 bg-neutral-900 mb-4" />
    <h3
      className="offering-title leading-[0.98] text-neutral-900"
      style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
    >
      {titleLine1}
      <br />
      {titleLine2}
    </h3>
    <div className="w-12 h-[2px] bg-[#CCFF00] mt-4 mb-4" />
    <p className="text-sm lg:text-base text-neutral-700 leading-relaxed max-w-md">{desc}</p>

    <div className="mt-8 border-b border-neutral-300">
      {services.map((s) => (
        <ServiceRow key={s.anchor} s={s} />
      ))}
    </div>
  </div>
);

const HowToWorkTogether = () => {
  const { language } = useLanguage();
  const c = servicesContent[language];

  return (
    <section id="services" className="py-24 lg:py-28 bg-[#faf7ee]/40 overflow-hidden" data-testid="how-to-work-together">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Intro row: text left, editorial image right */}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              className="lg:col-span-7 space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[11px] tracking-[0.4em] uppercase text-neutral-500">{c.howEyebrow}</p>
              <h2
                className="offering-title leading-[0.92] text-neutral-900"
                style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
              >
                {c.howTitleLine1}
                <br />
                {c.howTitleLine2}
              </h2>
              <p className="text-base lg:text-lg text-neutral-700 leading-relaxed max-w-xl">{c.howIntro}</p>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-full overflow-hidden" style={{ aspectRatio: '4 / 5' }}>
                <img
                  src={c.howImage}
                  alt="Soulboard styling"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={800}
                  height={1000}
                />
              </div>
            </motion.div>
          </div>

          {/* Two-column service overview */}
          <div className="grid lg:grid-cols-2 gap-x-16 gap-y-16 mt-20 lg:mt-24">
            <ServiceColumn
              number={c.col1Number}
              titleLine1={c.col1TitleLine1}
              titleLine2={c.col1TitleLine2}
              desc={c.col1Desc}
              services={c.col1Services}
              testid="index-column-discover"
            />
            <ServiceColumn
              number={c.col2Number}
              titleLine1={c.col2TitleLine1}
              titleLine2={c.col2TitleLine2}
              desc={c.col2Desc}
              services={c.col2Services}
              testid="index-column-handson"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToWorkTogether;
